import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";

import Button from "@material-ui/core/Button";
import Divider from "@material-ui/core/Divider";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import TextField from "@material-ui/core/TextField";
import { withStyles } from "@material-ui/core/styles";

import {
  fetchRedirectionParams,
  fetchRedirectionParamsForVpos
} from "../../../actions/redirect";
import { setCardData } from "../../../actions/card";
import { FormatAmount } from "../../../utils/Formatter";
import { calculateAmount } from "../../../utils/SurchargeUtils";
import {
  getCardDisplayNameForScheme,
  getCardIconForScheme
} from "../../../utils/BinUtils";

import "./SavedCard.css";

const styles = theme => ({
  payBtnColor: {
    color: theme.palette.success.main,
    borderColor: theme.palette.success.main,
    border: `solid`
  }
});

const isFormValid = form => {
  if (!form.cvv) {
    return { isInvalid: true };
  }
  if (form.cvv.length < 3) {
    return { isInvalid: true, errorMsg: "Too short" };
  }
  return { isInvalid: false };
};

export const SavedCardList = ({
  charge,
  dispatch,
  classes,
  history,
  isVpos
}) => {
  const {
    customerSavedCards,
    due,
    currency,
    paymentFamilies,
    feeRenderMode
  } = charge;

  const totalAmount = FormatAmount(
    calculateAmount(
      due,
      history.location.pathname,
      paymentFamilies.surcharges,
      feeRenderMode
    ),
    currency
  );
  const [form, setForm] = useState({
    cvv: "",
    id: customerSavedCards[0].id
  });
  const [validation, setValidation] = useState({ isInvalid: false });
  const cvvRef = React.createRef();
  useEffect(() => {
    cvvRef.current.focus();
    setValidation({ isInvalid: false });
    // Disabling eslint for the next line, following it actually causes a ui bug
    // eslint-disable-next-line
  }, [form.id]);

  const validateForm = () => {
    const validationResult = isFormValid(form);
    setValidation(validationResult);
    return validationResult;
  };

  function cardInfo(scheme, maskedPan, expiryDate, title) {
    const cardNumber = ` ••••${maskedPan}`;
    return (
      <>
        <div>
          <span className="saved-card-number">
            <span className="saved-card-name">
              {getCardDisplayNameForScheme(scheme)}
            </span>
            {cardNumber}
          </span>
          {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
          <span className="saved-card-exp-date">({expiryDate})</span>
        </div>
        <div>
          {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
          {title ? <span className="saved-card-title">{title}</span> : ""}
        </div>
      </>
    );
  }

  // eslint-disable-next-line no-unused-vars
  const submitSavedCardForm = e => {
    const validationResult = validateForm();
    if (validationResult.isInvalid) {
      cvvRef.current.focus();
      e.preventDefault();
    } else {
      // eslint-disable-next-line no-unused-expressions
      !isVpos
        ? dispatch(
            fetchRedirectionParams(charge.uuid, form, "savedcard", history)
          )
        : dispatch(
            fetchRedirectionParamsForVpos(
              charge.uuid,
              form,
              "savedcard",
              history
            )
          );

      // dispatch(fetchRedirectionParams(charge.uuid, form, "savedcard", history));
      form.type = "savedcard";
      dispatch(setCardData(form));
    }
  };

  const renderSavedCard = ({ id, scheme, maskedPan, expiry, title }) => {
    const expiryDate = `${expiry.substring(0, 2)}/${expiry.substring(2)}`;

    return (
      <ListItem
        button
        key={id}
        className="saved-card-item"
        onClick={() => {
          setForm({ ...form, id, cvv: "", maskedPan: "" });
          setValidation({ isInvalid: false });
        }}
      >
        <ListItemIcon className="saved-card-logo">
          <img
            src={getCardIconForScheme(scheme)}
            alt="card-logo"
            style={{ width: 25 }}
          />
        </ListItemIcon>
        <ListItemText
          primary={cardInfo(scheme, maskedPan, expiryDate, title)}
        />
        {id === form.id && (
          <TextField
            id="cvv"
            placeholder="CVV"
            value={form.cvv}
            onChange={e => {
              const { value } = e.target;
              if (/^\d{0,4}$/.test(value)) {
                setForm({ ...form, cvv: value, maskedPan });
                setValidation({ isInvalid: false });
              }
            }}
            onFocus={validateForm}
            inputRef={cvvRef}
            error={validation.isInvalid}
            helperText={validation.errorMsg}
            className="saved-card-cvv"
            type="password"
            inputProps={{ style: { padding: "6px 8px", fontSize: 16 } }}
          />
        )}
      </ListItem>
    );
  };

  const savedCards = [];
  customerSavedCards.forEach((card, index) => {
    savedCards.push(renderSavedCard(card));
    if (customerSavedCards[index + 1]) {
      savedCards.push(<Divider key={card.id} />);
    }
  });
  return (
    <List
      component="nav"
      aria-labelledby="nested-list-subheader"
      align="center"
    >
      {savedCards}
      <Button
        id="pay-button"
        variant="outlined"
        color="primary"
        className={classes.payBtnColor}
        onClick={submitSavedCardForm}
      >
        Pay&nbsp;
        {totalAmount}
      </Button>
    </List>
  );
};

SavedCardList.propTypes = {
  charge: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    due: PropTypes.number.isRequired,
    currency: PropTypes.string.isRequired,
    customerId: PropTypes.number.isRequired,
    feeRenderMode: PropTypes.string,
    customerSavedCards: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        scheme: PropTypes.string.isRequired,
        maskedPan: PropTypes.string.isRequired,
        expiry: PropTypes.string.isRequired
      }).isRequired
    ).isRequired,
    paymentFamilies: PropTypes.shape({
      surcharges: PropTypes.arrayOf(PropTypes.shape())
    }).isRequired
  }).isRequired,
  isVpos: PropTypes.bool,
  dispatch: PropTypes.func.isRequired,
  history: PropTypes.shape().isRequired,
  classes: PropTypes.shape().isRequired
};

SavedCardList.defaultProps = {
  isVpos: false
};

function mapStateToProps({ redirectionReducer }) {
  return redirectionReducer;
}

export default withStyles(styles, { withTheme: true })(
  withRouter(connect(mapStateToProps)(SavedCardList))
);
